import { useCallback, useEffect, useState } from 'react';
import { UserPlus, X } from 'lucide-react';
import type { UserSummary } from '@bits2schema/shared';
import { api } from '../api/client';
import { useT } from '../i18n';
import { describeError } from '../utils/format';
import { Modal } from './Modal';

interface UsersPanelProps {
  onClose: () => void;
}

/** E14-US3 — vytvoření nového uživatele (kterýkoli přihlášený uživatel) + E14-US7 — výpis pro výběr editorů. */
export function UsersPanel({ onClose }: UsersPanelProps) {
  const t = useT();
  const [users, setUsers] = useState<UserSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { users: list } = await api.listUsers();
      setUsers(list);
      setError(null);
    } catch (caught) {
      setError(describeError(caught, t));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const createUser = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const { user } = await api.createUser(username, displayName, password);
      setUsername('');
      setDisplayName('');
      setPassword('');
      setSuccess(t('users.create.success', { name: user.displayName }));
      await load();
    } catch (caught) {
      setError(describeError(caught, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t('users.title')}
      onClose={onClose}
      wide
      actions={
        <button type="button" onClick={onClose}>
          <X size={16} />
          {t('common.close')}
        </button>
      }
    >
      <div className="stack">
        {error && <div className="notice notice--error">{error}</div>}

        {users === null ? (
          <p className="hint">{t('common.loading')}</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t('users.column.username')}</th>
                <th>{t('users.column.displayName')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.displayName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="card">
          <h2>{t('users.create.title')}</h2>
          {success && <div className="notice notice--success">{success}</div>}
          <div className="row">
            <div className="field">
              <label htmlFor="new-user-username">{t('users.create.usernameLabel')}</label>
              <input id="new-user-username" value={username} onChange={(event) => setUsername(event.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="new-user-display-name">{t('users.create.displayNameLabel')}</label>
              <input
                id="new-user-display-name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="new-user-password">{t('users.create.passwordLabel')}</label>
              <input
                id="new-user-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
            <button
              type="button"
              className="primary"
              disabled={busy || !username.trim() || !displayName.trim() || !password}
              onClick={() => void createUser()}
            >
              <UserPlus size={16} />
              {t('users.create.submit')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
